// acneScarsRemovalData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const palliative: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Best Palliative Care Services in Dubai | Elite Body Home Polyclinic",
            "description": "Compassionate palliative care in Dubai focusing on pain relief, symptom management and emotional support. FREE consultation available. Book your appointment today.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/palliative-care-dubai/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Best Palliative Care Services in Dubai | Elite Body Home Polyclinic",
                "description": "Compassionate palliative care in Dubai focusing on pain relief, symptom management and emotional support. FREE consultation available. Book your appointment today.",
                "url": "https://elitebodyhome.com/services/palliative-care-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Palliative-Care.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Palliative-Care.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Palliative Care Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Best Palliative Care Services in Dubai | Elite Body Home Polyclinic",
                "description": "Compassionate palliative care in Dubai focusing on pain relief, symptom management and emotional support. FREE consultation available. Book your appointment today.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Palliative-Care.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Palliative Care Dubai"],
        "hero": {
            "title": "Palliative Care Services",
            "subtitle": "",
            "description": "Don't we all know this, any disease that you suffer from, a tiny help can work a miracle! Essentially, palliative care is a medical specialty that focuses on improving the quality of life for those who have incurable conditions or diseases. Such palliative care is much sought after in Dubai where the emphasis is on keeping patients free from pain, reducing the symptoms, and addressing the emotional and psychological needs of the patient as well as the family."
        },
        "howItWorks": {
            "title": "Palliative Care Process",
            "description": "In Dubai, the palliative care process is designed to provide comprehensive support through specialized medical care, symptom management and emotional support for patients and their families."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "Initial Consultation",
                    "desc": "Discussion of medical history, current symptoms and individual preferences to assess physical, emotional and psychological needs.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "Developing Care Plan",
                    "desc": "Creating a personalized care plan including medical treatments, pain management and emotional support strategies.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "Symptom Management",
                    "desc": "Using medications, physical therapy and relaxation techniques to manage pain, fatigue, breathlessness and other symptoms.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Emotional Support",
                    "desc": "Providing counseling and spiritual support for patients and family members.",
                    "icon": "fa-solid fa-hands-holding-heart"
                },
                {
                    "title": "Continuous Monitoring",
                    "desc": "Regular assessments and adjustments to the care plan based on patient's evolving needs.",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Palliative Care",
            "subheading": "Why palliative care makes a difference",
            "para": "Palliative care has enormous value, and is designed to focus on the dual needs of patients and their families. We have listed a few benefits below:",
            "items": [
                {
                    "title": "Improved Quality of Life",
                    "desc": "Alleviates pain and other symptoms allowing patients to spend time on meaningful activities.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Emotional Support",
                    "desc": "Therapy for patients and families to address the emotional challenges of illness.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "Holistic Approach",
                    "desc": "Addresses physical, emotional, social and spiritual needs for complete care.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "Family Involvement",
                    "desc": "Guidance and education for families to better support their loved ones.",
                    "icon": "fa-solid fa-people-group"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "Palliative care is appropriate at any point in the trajectory of a serious illness. It is helpful for:",
            "items": [
                {
                    "title": "Chronic Conditions",
                    "desc": "Patients with cancer, heart failure, kidney disease or COPD.",
                    "icon": "fa-solid fa-lungs"
                },
                {
                    "title": "Neurological Diseases",
                    "desc": "Those with Parkinson's, multiple sclerosis or ALS.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "Post-Surgical Care",
                    "desc": "Patients recovering from major surgery or injury needing intensive care.",
                    "icon": "fa-solid fa-bed-pulse"
                },
                {
                    "title": "End-of-Life Care",
                    "desc": "Those requiring comfort-focused care prioritizing dignity.",
                    "icon": "fa-solid fa-hand-holding-heart"
                }
            ]
        },
        "preparationTips": {
            "heading": "How to Prepare",
            "subheading": "",
            "para": "Preparation helps make this sensitive transition easier and ensures better support. Here's how to prepare:",
            "items": [
                {
                    "title": "Consult Your Doctor",
                    "desc": "Discuss your condition with your primary physician who can refer you to palliative care services.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "Organize Medical Records",
                    "desc": "Gather test results, prescriptions and treatment plans for comprehensive assessment.",
                    "icon": "fa-solid fa-folder-open"
                },
                {
                    "title": "Involve Family Members",
                    "desc": "Create a strong support system by involving loved ones in the process.",
                    "icon": "fa-solid fa-people-roof"
                },
                {
                    "title": "Clarify Care Goals",
                    "desc": "Communicate your preferences about treatment and decision-making clearly.",
                    "icon": "fa-solid fa-list-check"
                }
            ]
        },
        "results": {
            "heading": "Expected Results",
            "subheading": "",
            "para": "The benefits of palliative care extend well beyond symptom management, enhancing both patient and family wellbeing:",
            "items": [
                {
                    "title": "Physical Comfort",
                    "desc": "Effective pain and symptom management enhances physical comfort.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "Mental Wellbeing",
                    "desc": "Reduced stress, anxiety and depression for patients and families.",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "Stronger Relationships",
                    "desc": "Open communication leads to improved relationships among all involved.",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "Informed Decisions",
                    "desc": "Patients and families make better-informed healthcare decisions.",
                    "icon": "fa-solid fa-clipboard-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "Aftercare Recommendations",
            "subheading": "",
            "para": "Continuity is key after palliative care sessions to ensure long term wellbeing:",
            "items": [
                {
                    "title": "Follow-Up Appointments",
                    "desc": "Schedule regular check-ins to assess progress and adjust care plans.",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "Support Groups",
                    "desc": "Connect with local or online support groups in Dubai.",
                    "icon": "fa-solid fa-people-line"
                },
                {
                    "title": "Self-Care Practices",
                    "desc": "Maintain physical exercise, balanced diet and relaxation techniques.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "Stay Connected",
                    "desc": "Maintain communication with providers about any changes in condition.",
                    "icon": "fa-solid fa-phone-volume"
                }
            ]
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "What is the cost of palliative care in Dubai?",
                    "answer": "Costs vary depending on required services like home care, consultations or specialized medical interventions. Many providers offer customized services to suit different budgets."
                },
                {
                    "question": "Does insurance cover palliative care in Dubai?",
                    "answer": "Most health insurance policies in Dubai cover palliative care services. We recommend checking with your insurance provider for specific coverage details."
                },
                {
                    "question": "Can I receive palliative care at home?",
                    "answer": "Yes, many providers offer home-based palliative care services, allowing patients to receive treatment in a familiar, comfortable environment."
                },
                {
                    "question": "Is palliative care only for terminal cases?",
                    "answer": "No, palliative care can benefit patients at any stage of serious illness, not just end-of-life. It can be provided alongside curative treatment."
                },
                {
                    "question": "Is palliative care available for children?",
                    "answer": "Yes, specialized pediatric palliative care services are available for children with serious illnesses, including support for the whole family."
                },
                {
                    "question": "How do I choose the best palliative care provider in Dubai?",
                    "answer": "Look for accredited providers with qualified experts who adopt a patient-centered approach and have good reputation in the field."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2025/01/Palliative-Care-Services-2.jpg",
            "beforeAfter": []
        },
        "product": {
            "id": 351,
            "title": "Palliative Care Consultation Package",
            "price": 0,
            "description": "Compassionate palliative care consultation including assessment, care planning and initial support. Our expert team focuses on improving quality of life through specialized medical care and emotional support."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "أفضل خدمات الرعاية التلطيفية في دبي | عيادات ايليت بودي هوم",
            "description": "رعاية تلطيفية شاملة في دبي تركز على تخفيف الألم وإدارة الأعراض والدعم النفسي. استشارة مجانية متاحة. احجز موعدك اليوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/palliative-care-dubai/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "أفضل خدمات الرعاية التلطيفية في دبي | عيادات ايليت بودي هوم",
                "description": "رعاية تلطيفية شاملة في دبي تركز على تخفيف الألم وإدارة الأعراض والدعم النفسي. استشارة مجانية متاحة. احجز موعدك اليوم.",
                "url": "https://elitebodyhome.com/ar/services/palliative-care-dubai/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Palliative-Care.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Palliative-Care.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "الرعاية التلطيفية دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "أفضل خدمات الرعاية التلطيفية في دبي | عيادات ايليت بودي هوم",
                "description": "رعاية تلطيفية شاملة في دبي تركز على تخفيف الألم وإدارة الأعراض والدعم النفسي. استشارة مجانية متاحة. احجز موعدك اليوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Palliative-Care.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "الرعاية التلطيفية دبي"],
        "hero": {
            "title": "خدمات الرعاية التلطيفية",
            "subtitle": "",
            "description": "يمكن للمساعدة البسيطة أن تُحدث فرقًا كبيرًا في مواجهة الأمراض! الرعاية التلطيفية هي تخصص طبي يهدف إلى تحسين جودة الحياة للأشخاص الذين يعانون من حالات أو أمراض غير قابلة للشفاء. تزداد الحاجة إلى هذا النوع من الرعاية في دبي، حيث يهتم بتخفيف الألم والأعراض، وتوفير الدعم، ومعالجة الجوانب النفسية لكلٍ من المريض وأفراد عائلته."
        },
        "howItWorks": {
            "title": "خطوات الرعاية التلطيفية",
            "description": "صُممت عملية الرعاية التلطيفية في دبي لتوفير دعم شامل ومتكامل من خلال الرعاية الطبية المتخصصة وإدارة الأعراض والدعم النفسي للمرضى وعائلاتهم."
        },
        "procedureSteps": {
            "heading": "",
            "para": "",
            "steps": [
                {
                    "title": "الاستشارة الأولية",
                    "desc": "مناقشة التاريخ الطبي، والأعراض الحالية، والتفضيلات الشخصية لتقييم الاحتياجات الجسدية والنفسية.",
                    "icon": "fa-solid fa-calendar-check"
                },
                {
                    "title": "وضع خطة الرعاية",
                    "desc": "وضع خطة رعاية مخصصة تشمل العلاجات الطبية، وإدارة الألم، واستراتيجيات الدعم النفسي.",
                    "icon": "fa-solid fa-file-medical"
                },
                {
                    "title": "إدارة الأعراض",
                    "desc": "استخدام الأدوية، والعلاج الطبيعي، وتقنيات الاسترخاء للسيطرة على الألم، والتعب، وضيق التنفس والأعراض الأخرى.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "الدعم النفسي",
                    "desc": "تقديم الاستشارات والدعم الروحي للمرضى وأفراد العائلة.",
                    "icon": "fa-solid fa-hands-holding-heart"
                },
                {
                    "title": "المتابعة المستمرة",
                    "desc": "تقييمات دورية وتعديلات على خطة الرعاية بناءً على تطور احتياجات المريض.",
                    "icon": "fa-solid fa-chart-line"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد الرعاية التلطيفية",
            "subheading": "لماذا تُحدث الرعاية التلطيفية فرقًا",
            "para": "توفر الرعاية التلطيفية العديد من الفوائد، حيث تضمن رعاية شاملة للمرضى وعائلاتهم. فيما يلي بعض أهم المزايا:",
            "items": [
                {
                    "title": "تحسين جودة الحياة",
                    "desc": "تخفيف الألم والأعراض الأخرى مما يمنح المرضى فرصة لقضاء وقتهم في أنشطة ذات معنى.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "الدعم النفسي",
                    "desc": "تقديم استشارات نفسية للمرضى وعائلاتهم لمساعدتهم على التعامل مع التحديات النفسية المصاحبة للمرض.",
                    "icon": "fa-solid fa-heart"
                },
                {
                    "title": "نهج شامل",
                    "desc": "معالجة الاحتياجات الجسدية والعاطفية والاجتماعية والروحية لضمان رعاية متكاملة.",
                    "icon": "fa-solid fa-hand-holding-medical"
                },
                {
                    "title": "مشاركة العائلة",
                    "desc": "توجيه وتوعية أفراد العائلة لتمكينهم من دعم أحبائهم بشكل أفضل.",
                    "icon": "fa-solid fa-people-group"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "تناسب الرعاية التلطيفية المرضى في مختلف مراحل الأمراض الخطيرة، وهي مفيدة للفئات التالية:",
            "items": [
                {
                    "title": "الأمراض المزمنة",
                    "desc": "المرضى الذين يعانون من السرطان، وفشل القلب، وأمراض الكلى، أو مرض الانسداد الرئوي المزمن.",
                    "icon": "fa-solid fa-lungs"
                },
                {
                    "title": "الأمراض العصبية",
                    "desc": "المصابون بمرض باركنسون، أو التصلب المتعدد، أو التصلب الجانبي الضموري.",
                    "icon": "fa-solid fa-brain"
                },
                {
                    "title": "ما بعد الجراحة",
                    "desc": "المرضى الذين خضعوا لعمليات جراحية كبرى أو إصابات تتطلب رعاية مكثفة.",
                    "icon": "fa-solid fa-bed-pulse"
                },
                {
                    "title": "رعاية نهاية الحياة",
                    "desc": "الأشخاص الذين يحتاجون إلى رعاية تركز على الراحة والاحترام.",
                    "icon": "fa-solid fa-hand-holding-heart"
                }
            ]
        },
        "preparationTips": {
            "heading": "كيفية الاستعداد",
            "subheading": "",
            "para": "يضمن التحضير الجيد للرعاية التلطيفية الحصول على خدمة سلسة ودعمًا أكثر فعالية. إليك كيفية الاستعداد:",
            "items": [
                {
                    "title": "استشارة الطبيب",
                    "desc": "ناقش حالتك مع طبيبك الأساسي الذي يمكنه تحويلك إلى خدمات الرعاية التلطيفية.",
                    "icon": "fa-solid fa-user-doctor"
                },
                {
                    "title": "تنظيم السجلات الطبية",
                    "desc": "اجمع نتائج الفحوصات، والوصفات الطبية، وخطط العلاج لتقييم شامل.",
                    "icon": "fa-solid fa-folder-open"
                },
                {
                    "title": "إشراك أفراد العائلة",
                    "desc": "أنشئ نظام دعم قوي من خلال إشراك الأحباء في العملية.",
                    "icon": "fa-solid fa-people-roof"
                },
                {
                    "title": "توضيح أهداف الرعاية",
                    "desc": "عبّر عن تفضيلاتك فيما يتعلق بالعلاج واتخاذ القرارات بوضوح.",
                    "icon": "fa-solid fa-list-check"
                }
            ]
        },
        "results": {
            "heading": "النتائج المتوقعة",
            "subheading": "",
            "para": "تتجاوز فوائد الرعاية التلطيفية السيطرة على الأعراض، حيث تُحدث تأثيرًا إيجابيًا على كل من المريض وعائلته:",
            "items": [
                {
                    "title": "راحة جسدية",
                    "desc": "إدارة فعالة للألم والأعراض مما يعزز الراحة الجسدية.",
                    "icon": "fa-solid fa-bed"
                },
                {
                    "title": "صحة نفسية أفضل",
                    "desc": "تقليل التوتر والقلق والاكتئاب للمرضى والعائلات.",
                    "icon": "fa-solid fa-head-side-virus"
                },
                {
                    "title": "علاقات أقوى",
                    "desc": "التواصل المفتوح يؤدي إلى تحسين العلاقات بين جميع الأطراف.",
                    "icon": "fa-solid fa-people-arrows"
                },
                {
                    "title": "قرارات مستنيرة",
                    "desc": "يتخذ المرضى والعائلات قرارات رعاية صحية أفضل بناءً على فهم واضح.",
                    "icon": "fa-solid fa-clipboard-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "نصائح ما بعد الرعاية",
            "subheading": "",
            "para": "يعد الحفاظ على استمرارية الرعاية بعد الجلسات التلطيفية أمرًا ضروريًا لضمان التحسن على المدى الطويل:",
            "items": [
                {
                    "title": "مواعيد المتابعة",
                    "desc": "حدد مواعيد متابعة منتظمة لتقييم التقدم وإجراء التعديلات على خطة الرعاية.",
                    "icon": "fa-solid fa-calendar-days"
                },
                {
                    "title": "مجموعات الدعم",
                    "desc": "تواصل مع مجموعات الدعم المحلية أو عبر الإنترنت في دبي.",
                    "icon": "fa-solid fa-people-line"
                },
                {
                    "title": "ممارسات العناية الذاتية",
                    "desc": "حافظ على ممارسة الرياضة، نظام غذائي متوازن وتقنيات الاسترخاء.",
                    "icon": "fa-solid fa-heart-pulse"
                },
                {
                    "title": "البقاء على تواصل",
                    "desc": "حافظ على التواصل مع مقدمي الرعاية بشأن أي تغييرات في الحالة.",
                    "icon": "fa-solid fa-phone-volume"
                }
            ]
        },
        "faqData": {
            "heading": "الأسئلة الشائعة",
            "subheading": "",
            "items": [
                {
                    "question": "ما تكلفة الرعاية التلطيفية في دبي؟",
                    "answer": "تختلف التكلفة حسب نوع الخدمات المطلوبة، مثل الرعاية المنزلية، أو الاستشارات، أو التدخلات الطبية المتخصصة. تقدم العديد من الجهات خدمات مخصصة تناسب مختلف الميزانيات."
                },
                {
                    "question": "هل تضم شركات التأمين الرعاية التلطيفية في دبي؟",
                    "answer": "تغطي معظم وثائق التأمين الصحي في دبي خدمات الرعاية التلطيفية. يفضل مراجعة شركة التأمين الخاصة بك لمعرفة التفاصيل."
                },
                {
                    "question": "هل يمكنني الحصول على الرعاية التلطيفية في المنزل؟",
                    "answer": "نعم، توفر العديد من الجهات خدمات الرعاية التلطيفية المنزلية، مما يسمح للمرضى بتلقي العلاج في بيئة مألوفة ومريحة."
                },
                {
                    "question": "هل الرعاية التلطيفية مخصصة فقط للحالات المرضية في مراحلها الأخيرة؟",
                    "answer": "لا، يمكن الاستفادة من الرعاية التلطيفية في أي مرحلة من مراحل المرض، وليس فقط في نهاية الحياة. ويمكن تقديمها جنبًا إلى جنب مع العلاج الشفائي."
                },
                {
                    "question": "هل يمكن للأطفال الحصول على الرعاية التلطيفية؟",
                    "answer": "نعم، هناك خدمات رعاية تلطيفية للأطفال المصابين بأمراض خطيرة، وتشمل دعمًا متخصصًا لكل من الطفل وعائلته."
                },
                {
                    "question": "كيف أختار أفضل مقدم لخدمات الرعاية التلطيفية في دبي؟",
                    "answer": "ابحث عن جهة معتمدة، تضم خبراء مؤهلين يتمتعون بسمعة طيبة، وتتبنى نهجًا يركز على احتياجات المريض."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}